import CustomIcon from "components/CustomIcon";
import { CertificatesForm } from "components/ResumeForm/CertificatesForm";
import { CustomForm } from "components/ResumeForm/CustomForm";
import { ResumeAnalysis } from "lib/resume-scoring/resume-analysis/resume-analyse-types";
import Image from "next/image";
import { useState } from "react";
import { SectionHeader } from "../common/SectionHeader";
import { AwardsForm } from "components/ResumeForm/AwardsForm";
import { LanguagesForm } from "components/ResumeForm/LanguagesForm";
import { RepeatedSentences } from "../common/RepeatedSentences";
import { RepeatedActionVerbs } from "../common/RepeatedActionVerbs";
import { DateStructureConsistent } from "../common/DateStructureNotConsistent";

export const OverallSection = ({
  score,
  maxScore,
  analysis,
}: {
  score: number;
  maxScore: number;
  analysis: ResumeAnalysis["overallAnalysis"];
}) => {
  const [isAddCertificateOpen, setIsAddCertificateOpen] = useState(false);
  const [isAddCustomSectionOpen, setIsAddCustomSectionOpen] = useState(false);
  const [isAddLanguageOpen, setIsAddLanguageOpen] = useState(false);
  const [isAddAwardOpen, setIsAddAwardOpen] = useState(false);

  return (
    <div className="relative mb-4 flex max-h-[88.5vh] flex-col overflow-hidden rounded-[12px] bg-white px-2 pb-3 shadow-md xl:px-[21px]">
      <SectionHeader title="Miscellaneous" score={score} maxScore={maxScore} />

      <span className="sticky top-0 z-10 mx-auto my-2 hidden cursor-pointer  items-center justify-center gap-3 rounded-xl bg-gray-100 px-[16px] py-[4px] text-center text-sm font-semibold text-black shadow-md transition-opacity hover:opacity-90 md:text-lg lg:flex xl:px-[24px] xl:py-[8px] xl:text-xl">
        Miscellaneous
      </span>

      {/* Body */}
      <div className="scrollbar-hide flex flex-1 flex-col gap-[25px] overflow-auto rounded-xl lg:gap-[30px] lg:p-4 xl:gap-[40px]">
        {/* */}
        <WordCountBar wordCount={analysis.totalNumberOfWords} />
        <BulletPointBar bulletCount={analysis.totalNumberOfBulletPoints} />

        {/* Repeated Sentences */}
        {analysis.repeatedSentences.phrases.length > 0 && (
          <RepeatedSentences sentences={analysis.repeatedSentences.phrases} />
        )}

        {/* Repeated Action Verbs */}
        {analysis.repeatedActionVerbs.verbs.length > 0 && (
          <RepeatedActionVerbs
            verbs={analysis.repeatedActionVerbs.verbs}
            verbsWithCounts={analysis.repeatedActionVerbs.verbsWithCounts}
          />
        )}

        {/* Date Structure */}

        <DateStructureConsistent
          isDateStructureConsistent={analysis.isDateStructureConsistent}
        />

        <div className="relative mt-4 flex w-full items-center justify-center rounded-xl bg-[#FFF6F6] p-2 md:p-3 text-center font-montserrat shadow-sm md:mt-3 lg:px-2">
          {/* Badge / Icon Title in the top-left (half-out style) */}
          <div className="absolute -top-6 left-4 flex items-center gap-3 rounded-xl bg-white px-4 py-2 text-base font-semibold shadow-md">
            <Image
              src="/icons/score-check/additionaldata.svg"
              width={28}
              height={28}
              alt="date icon"
              className="h-6 w-6"
            />
            <span className="text-base">Additional Data</span>
          </div>

          <div className="mx-auto flex max-w-4xl flex-col items-center justify-center gap-6 px-4 py-6">
            <h3 className="text-sm font-medium text-[#000000] lg:text-xl">
              No Additional Data found.
            </h3>
            <p>(Not affecting score)</p>
            <div className="flex items-center justify-center">
              <Image
                src="/assets/resumeScore/AdditionData.png"
                alt="illustration"
                width={260}
                height={160}
                className="object-contain"
              />
            </div>

            <p className="max-w-3xl text-center font-medium text-xs text-black lg:text-base">
              Additional details like certificates or achievements can make your resume stand out by showing extra skills and initiative.
            </p>

            {/* Certificates Add */}

            <div className="flex w-full flex-col items-center gap-3 rounded-xl bg-[#EAF1F4] p-[15px] sm:p-[20px] lg:p-[25px]">
              <div className="flex w-full items-center justify-between">
                <span className="text-sm font-semibold text-gray-600 sm:text-base md:text-lg lg:text-xl">
                  Add Certificates
                </span>
                <button
                  onClick={() => setIsAddCertificateOpen(!isAddCertificateOpen)}
                  className="rounded-lg bg-white px-3 py-1 text-xs font-medium text-[#458ef7] shadow-md hover:bg-gray-100 sm:px-4 sm:py-2 sm:text-sm md:text-base lg:px-5"
                >
                  {isAddCertificateOpen ? "Close" : "Add"}
                </button>
              </div>
            </div>
            {isAddCertificateOpen && (
              // <div>
                <CertificatesForm />
                // <SaveButton isAddOpen={isAddCertificateOpen} />
              // </div>
            )}

            <div className="flex w-full flex-col items-center gap-3 rounded-xl bg-[#EAF1F4] p-[15px] sm:p-[20px] lg:p-[25px]">
              <div className="flex w-full items-center justify-between">
                <span className="text-sm font-semibold text-gray-600 sm:text-base md:text-lg lg:text-xl">
                  Add Awards
                </span>
                <button
                  onClick={() => setIsAddAwardOpen(!isAddAwardOpen)}
                  className="rounded-lg bg-white px-3 py-1 text-xs font-medium text-[#458ef7] shadow-md hover:bg-gray-100 sm:px-4 sm:py-2 sm:text-sm md:text-base lg:px-5"
                >
                  {isAddAwardOpen ? "Close" : "Add"}
                </button>
              </div>
            </div>
            {isAddAwardOpen && 
              // <div>
                <AwardsForm />
                /* <SaveButton isAddOpen={isAddAwardOpen} /> */
              /* </div> */
            }

            <div className="flex w-full flex-col items-center gap-3 rounded-xl bg-[#EAF1F4] p-[15px] sm:p-[20px] lg:p-[25px]">
              <div className="flex w-full items-center justify-between">
                <span className="text-sm font-semibold text-gray-600 sm:text-base md:text-lg lg:text-xl">
                  Add Languages
                </span>
                <button
                  onClick={() => setIsAddLanguageOpen(!isAddLanguageOpen)}
                  className="rounded-lg bg-white px-3 py-1 text-xs font-medium text-[#458ef7] shadow-md hover:bg-gray-100 sm:px-4 sm:py-2 sm:text-sm md:text-base lg:px-5"
                >
                  {isAddLanguageOpen ? "Close" : "Add"}
                </button>
              </div>
            </div>
            {isAddLanguageOpen && (
              // <div>
                <LanguagesForm />
                // <SaveButton isAddOpen={isAddLanguageOpen} />
              // </div>
            )}

            {/* Custom Section Add */}
            <div className="flex w-full flex-col items-center gap-3 rounded-xl bg-[#EAF1F4] p-[15px] sm:p-[20px] lg:p-[25px]">
              <div className="flex w-full items-center justify-between">
                <span className="text-sm font-semibold text-gray-600 sm:text-base md:text-lg lg:text-xl">
                  Add Custom section
                </span>
                <button
                  onClick={() =>
                    setIsAddCustomSectionOpen(!isAddCustomSectionOpen)
                  }
                  className="rounded-lg bg-white px-3 py-1 text-xs font-medium text-[#458ef7] shadow-md hover:bg-gray-100 sm:px-4 sm:py-2 sm:text-sm md:text-base lg:px-5"
                >
                  {isAddCustomSectionOpen ? "Close" : "Add"}
                </button>
              </div>
            </div>
            {isAddCustomSectionOpen && (
              // <div>
                <CustomForm />
                /* <SaveButton isAddOpen={isAddCustomSectionOpen} /> */
              /* </div> */
            )}
          </div>
        </div>
      </div>

      {/* Hide scrollbar styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

/* ----------------------
   Word Count Bar 
----------------------- */
const MIN_WORDS = 100;
const MAX_WORDS = 1000;
function WordCountBar({ wordCount }: { wordCount: number }) {
  const percent = ((wordCount - MIN_WORDS) / (MAX_WORDS - MIN_WORDS)) * 100;
  const clampPercent = Math.max(0, Math.min(100, percent));
  return (
    <div className="mt-5 flex w-full flex-col gap-y-5 rounded-lg bg-[#555555] p-5 text-white">
      <div className="mb-8 flex w-full items-center justify-between text-lg font-semibold md:text-xl lg:text-2xl">
        <span>Word Count: {wordCount}</span>
        <div className="group relative">
          <div className="flex h-6 w-6 cursor-help items-center justify-center rounded-full bg-blue-500 md:h-7 md:w-7">
            <span className="text-sm font-bold text-white md:text-base">i</span>
          </div>
          <div className="invisible absolute bottom-full right-0 z-10 mb-2 w-60 rounded bg-white p-3 text-sm text-gray-800 shadow-lg group-hover:visible">
            <p className="font-medium">
              Optimal word count is between 400 to 600 words.
            </p>
          </div>
        </div>
      </div>
      <div className="relative mb-4 h-8">
        <div
          className="absolute top-1/2 h-8 w-full -translate-y-1/2 transform rounded-full"
          style={{
            background:
              "linear-gradient(to right, #e44133 0%, #e4761b 30%, #6cc92c 60%, #135321 100%)",
          }}
        />
        <div
          className="absolute bottom-5 flex flex-col items-start justify-start"
          style={{ left: `calc(${clampPercent}%)` }}
        >
          <div className="ml-[-5px] flex items-center justify-start gap-1">
            <div className="h-4 w-4 rounded-full bg-[#45CA17]" />
            {wordCount}
          </div>
          <div className="h-8 w-1 bg-[#018950]" />
        </div>
      </div>
      <div className="flex justify-between px-1 text-xs md:text-sm">
        <span>Too less</span>
        <span>Too many</span>
      </div>
    </div>
  );
}

/* ----------------------
   Bullet Point Bar 
----------------------- */
const MIN_BULLETS = 1;
const MAX_BULLETS = 40;
function BulletPointBar({ bulletCount }: { bulletCount: number }) {
  const percent =
    ((bulletCount - MIN_BULLETS) / (MAX_BULLETS - MIN_BULLETS)) * 100;
  const clampPercent = Math.max(0, Math.min(100, percent));
  return (
    <div className="flex w-full flex-col gap-y-6 rounded-lg bg-gray-300 p-5 text-gray-700">
      <div className="mb-8 flex w-full items-center justify-between text-lg font-semibold md:text-xl lg:text-2xl">
        <span>Bullet points: {bulletCount}</span>
        <div className="group relative">
          <div className="flex h-6 w-6 cursor-help items-center justify-center rounded-full bg-blue-500 md:h-7 md:w-7">
            <span className="text-sm font-bold text-white md:text-base">i</span>
          </div>
          <div className="invisible absolute bottom-full right-0 z-10 mb-2 w-60 rounded bg-white p-3 text-sm text-gray-800 shadow-lg group-hover:visible">
            <p className="font-medium">
              Optimal bullet point count is between 12 to 20 points.
            </p>
          </div>
        </div>
      </div>
      <div className="relative mb-4 h-8">
        <div
          className="absolute top-1/2 h-8 w-full -translate-y-1/2 transform rounded-full"
          style={{
            background:
              "linear-gradient(to right, #663300 0%, #187bcd 60%, #1a1a66 100%)",
          }}
        />
        <div
          className="absolute bottom-5 flex flex-col items-start justify-start"
          style={{ left: `calc(${clampPercent}%)` }}
        >
          <div className="ml-[-5px] flex items-center justify-start gap-1">
            <div className="h-4 w-4 rounded-full bg-blue-500" />
            {bulletCount}
          </div>
          <div className="h-8 w-1 bg-blue-500" />
        </div>
      </div>
      <div className="flex justify-between px-1 text-xs md:text-sm">
        <span>Too less</span>
        <span>Too many</span>
      </div>
    </div>
  );
}

{
  /* <div className="bg-[#555555] mt-5 text-white flex flex-col rounded-lg p-5 gap-y-5 w-full">
      <div className="text-lg md:text-xl lg:text-2xl mb-4 font-semibold">
        Word count: {wordCount}
      </div>
      <div className="relative h-8 mb-4">
        <div
          className="absolute top-1/2 transform -translate-y-1/2 h-8 rounded-full w-full"
          style={{
            background:
              "linear-gradient(to right, #e44133 0%, #e4761b 30%, #6cc92c 60%, #135321 100%)",
          }}
        />
        <div
          className="absolute flex flex-col items-center bottom-5"
          style={{ left: `calc(${clampPercent}% - 0.75rem)` }}
        >
          <div className="flex justify-start items-center ml-[-7px]">
          <div className="w-4 h-4 rounded-full bg-blue-500" />
          {wordCount}
          </div>
        </div>
      </div>
      <div className="flex justify-between text-xs md:text-sm px-1">
        <span>Too less</span>
        <span>Too many</span>
      </div>
    </div> */
}
